import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import {
  ALERT_LOCK,
  AlertClaim,
  BusMessage,
  MessageAlert,
  TabState,
  getAlertBus,
  routeAlert,
} from './alertBus';
import {
  clearToast,
  dismissToast,
  showMessageToast,
  toastStore,
  ToastActions,
  updateToastClaim,
} from './MessageToast';
import { handledReason, mergeAlert } from './toastStore';
import { NotificationPreferences } from './notificationPreferences';
import { StaffCoverage, coversMarket } from './staffMarkets';
import { CommunicationInsert, InboxItem, alertCopy, isAlertableInsert } from './types';

/** Retry a view lookup once, this long after a miss (plan §2). */
const RESOLVE_RETRY_MS = 2000;
/** The fastest a reply or Done elsewhere takes a toast down (T-060 pacing). */
const RECHECK_MS = 5000;

const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

/**
 * The conversation a new message landed on, straight from `inbox_items` — so
 * the alert's key, owner and wording are exactly what the list and the badge
 * show, with no second copy of the view's logic in the client.
 */
async function resolveConversation(commId: string): Promise<InboxItem | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const { data } = await supabase
      .from('inbox_items')
      .select('*')
      .eq('last_message_id', commId)
      .maybeSingle();
    if (data) return data as InboxItem;
    // The insert can race the link to its client or lead.
    if (attempt === 0) await sleep(RESOLVE_RETRY_MS);
  }
  return null;
}

const profileNames = new Map<string, string | null>();

async function profileName(id: string): Promise<string | null> {
  if (profileNames.has(id)) return profileNames.get(id) ?? null;
  const { data } = await supabase.from('profiles').select('name').eq('id', id).maybeSingle();
  const name = (data as { name?: string } | null)?.name ?? null;
  profileNames.set(id, name);
  return name;
}

function desktopReady(prefs: NotificationPreferences): boolean {
  return (
    prefs.desktop_enabled &&
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted'
  );
}

/**
 * New-message alerts for the conversations that are mine, or nobody's (plan
 * §1–§4). Runs in every admin tab; only the leader tab listens and decides.
 *
 * - Every tab reports focus / visibility / open thread on the bus, and shows
 *   the toasts the leader addresses to it.
 * - The leader (one per browser, via `navigator.locks`) takes each new
 *   inbound SMS or unanswered call, resolves it through the view, keeps it only
 *   if it is still waiting and is mine (or unclaimed in a market I cover, if I
 *   want those), then
 *   routes it: a toast in the focused tab, else a desktop notification — never
 *   both, never twice, and nothing when a visible dock already has the thread
 *   open (its arrival flash is enough).
 * - While a toast is up (they stay until dealt with, T-076), the leader
 *   watches its conversation: a reply or Done elsewhere takes the toast down
 *   with a word on why; a lead being taken turns "Unclaimed" into "Megan took
 *   it" and then goes. A second message merges into the toast already up —
 *   moving it to the focused tab if that's another one.
 * - Closing a toast (✕, Open, Done, opening the thread) or Clear all goes to
 *   every tab.
 *
 * `prefs` and `coverage` are null only while they load; the hook waits for them.
 */
export function useMessageAlerts({
  me,
  prefs,
  coverage,
  openKey,
  openConversation,
}: {
  me: { id: string; name: string | null } | null;
  prefs: NotificationPreferences | null;
  /** The markets I cover (staff_markets): scopes unclaimed alerts only. */
  coverage: StaffCoverage | null;
  /** The thread this tab's dock is showing, if the dock is open. */
  openKey: string | null;
  openConversation: (item: InboxItem) => void;
}) {
  const enabled = !!me && !!prefs && !!coverage;
  const bus = getAlertBus();
  const [isLeader, setIsLeader] = useState(false);

  // Read at event time, so a settings change or a newly opened thread never
  // tears down the realtime subscription.
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const coverageRef = useRef(coverage);
  coverageRef.current = coverage;
  const openKeyRef = useRef(openKey);
  openKeyRef.current = openKey;
  const openRef = useRef(openConversation);
  openRef.current = openConversation;
  const meRef = useRef(me);
  meRef.current = me;
  const focusedAtRef = useRef(document.hasFocus() ? Date.now() : 0);

  const selfState = (focused = document.hasFocus()): TabState => ({
    tabId: bus.tabId,
    focused,
    visible: document.visibilityState === 'visible',
    openKey: openKeyRef.current,
    focusedAt: focusedAtRef.current,
  });

  const toastActions = (): ToastActions | null => {
    const m = meRef.current;
    if (!m) return null;
    return {
      me: m,
      onOpen: (alert) => openRef.current(alert.item),
    };
  };

  // ── Every tab: say where I am, show what I'm sent ──────────────────────────
  useEffect(() => {
    if (!enabled) return;

    const report = (focused?: boolean) => {
      if (focused) focusedAtRef.current = Date.now();
      bus.post({ type: 'state', state: selfState(focused) });
    };
    const onFocus = () => report(true);
    const onBlur = () => report(false);
    const onVisibility = () => report();
    const onHide = () =>
      bus.post({ type: 'state', state: { ...selfState(false), visible: false, closed: true } });

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onHide);

    const off = bus.subscribe((msg: BusMessage) => {
      if (msg.type === 'hello') {
        report();
        // A new leader: tell it which toasts are up here, so it keeps watching them.
        const alerts = toastStore.live().map((e) => e.alert);
        if (alerts.length) bus.post({ type: 'toasts-up', tabId: bus.tabId, alerts });
      } else if (msg.type === 'toast' && msg.to === bus.tabId) {
        const actions = toastActions();
        if (actions) showMessageToast(msg.alert, actions);
      } else if (msg.type === 'alert-update') updateToastClaim(msg.key, msg.claim);
      else if (msg.type === 'alert-clear') clearToast(msg.key, msg.reason);
      else if (msg.type === 'toast-closed') toastStore.remove(msg.key);
      else if (msg.type === 'toasts-cleared') toastStore.removeAll();
      else if (msg.type === 'toast-drop' && msg.keep !== bus.tabId) toastStore.remove(msg.key);
    });

    report();
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // The open thread is part of this tab's state. Opening it is dealing with
  // it: its toast goes, in whichever tab it's in.
  useEffect(() => {
    if (enabled) bus.post({ type: 'state', state: selfState() });
    if (enabled && openKey) dismissToast(openKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, openKey]);

  // ── Leader election ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const locks = (navigator as any).locks as LockManager | undefined;
    if (!locks) {
      // No Web Locks (very old browser): each tab alerts for itself.
      setIsLeader(true);
      return () => setIsLeader(false);
    }
    const abort = new AbortController();
    let release: (() => void) | undefined;
    locks
      .request(ALERT_LOCK, { signal: abort.signal }, () => {
        setIsLeader(true);
        // Held until this tab goes away; the browser then passes it on.
        return new Promise<void>((resolve) => {
          release = resolve;
        });
      })
      .catch(() => undefined); // AbortError on unmount while still waiting
    return () => {
      abort.abort();
      release?.();
      setIsLeader(false);
    };
  }, [enabled]);

  // ── Leader: watch, decide, route ───────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !isLeader || !me) return;

    const others = new Map<string, TabState>();
    /** Communication ids already handled — realtime reconnects can replay. */
    const seen = new Set<string>();
    /**
     * Toasts currently up, by conversation, and the tab each is in — so they
     * can be merged into, updated or cleared. No expiry: a toast stays until
     * it's dealt with, and so does the watch on it.
     */
    const active = new Map<string, { alert: MessageAlert; tabId: string }>();
    // This tab's own toasts, if it has just taken over from a closed leader.
    toastStore.live().forEach((e) => active.set(e.alert.key, { alert: e.alert, tabId: bus.tabId }));

    const offBus = bus.subscribe((msg) => {
      if (msg.type === 'state') {
        if (msg.state.closed) {
          others.delete(msg.state.tabId);
          // Its toasts closed with it.
          active.forEach((a, key) => {
            if (a.tabId === msg.state.tabId) active.delete(key);
          });
        } else others.set(msg.state.tabId, msg.state);
      } else if (msg.type === 'toast-closed') {
        active.delete(msg.key);
      } else if (msg.type === 'toasts-cleared') {
        active.clear();
      } else if (msg.type === 'toasts-up') {
        msg.alerts.forEach((alert) => {
          if (!active.has(alert.key)) active.set(alert.key, { alert, tabId: msg.tabId });
        });
      } else if (msg.type === 'alert-update') {
        const a = active.get(msg.key);
        if (a) a.alert = { ...a.alert, claim: msg.claim };
      }
    });
    // Learn every open tab's state now that this one decides.
    bus.post({ type: 'hello' });

    const sendToast = (tabId: string, alert: MessageAlert) => {
      if (tabId === bus.tabId) {
        const actions = toastActions();
        if (actions) showMessageToast(alert, actions);
      } else {
        bus.post({ type: 'toast', to: tabId, alert });
      }
    };

    const deliver = (fresh: MessageAlert, item: InboxItem, prefs: NotificationPreferences) => {
      const tabs = [selfState(), ...Array.from(others.values())];
      const route = routeAlert(fresh.key, tabs, {
        toasts: prefs.toasts_enabled,
        desktop: desktopReady(prefs),
      });
      if (route.kind === 'none') return;

      // Its toast is still up: this message joins it ("3 new messages").
      const prev = active.get(fresh.key);
      const alert = mergeAlert(prev?.alert, fresh);

      if (route.kind === 'toast') {
        active.set(alert.key, { alert, tabId: route.tabId });
        // Up in another tab: it moves to this one rather than showing twice.
        if (prev && prev.tabId !== route.tabId) {
          bus.broadcast({ type: 'toast-drop', key: alert.key, keep: route.tabId });
        }
        sendToast(route.tabId, alert);
      } else {
        // The desktop notification announces it; the toast already up just
        // catches up, quietly.
        if (prev) {
          const quiet = { ...alert, sound: false };
          active.set(alert.key, { alert: quiet, tabId: prev.tabId });
          sendToast(prev.tabId, quiet);
        }
        const { title, context, preview } = alertCopy(item);
        const options: NotificationOptions & { renotify?: boolean } = {
          // One notification per conversation, updated in place: a family
          // sending three texts leaves one entry in Notification Centre.
          tag: `inbox:${alert.key}`,
          body: context ? `${context}\n${preview}` : preview,
          icon: '/logo192.png',
          renotify: false,
          silent: !prefs.sound_enabled,
        };
        const n = new Notification(
          alert.kind === 'unclaimed' ? `${title} · Unclaimed lead` : title,
          options,
        );
        n.onclick = () => {
          window.focus();
          openRef.current(item);
          n.close();
        };
      }
    };

    const onInsert = async (row: CommunicationInsert) => {
      if (!row?.id || seen.has(row.id)) return;
      seen.add(row.id);
      if (!isAlertableInsert(row)) return;
      const prefs0 = prefsRef.current;
      if (!prefs0 || !prefs0.channels.includes(row.channel as any)) return;

      const item = await resolveConversation(row.id);
      const prefs = prefsRef.current;
      // Gone, or a colleague already answered / marked it done.
      if (!item || !item.is_unreplied || !prefs) return;

      // Mine: always, whatever the market. Unclaimed: only in a market I
      // cover (an unknown market is everyone's), and only if I want those.
      const mine = item.assignee_id === me.id;
      const unclaimed = item.assignee_id == null;
      const cov = coverageRef.current;
      const inMyMarkets = !!cov && coversMarket(cov, item.market);
      if (!mine && !(unclaimed && prefs.unclaimed_enabled && inMyMarkets)) return;

      deliver(
        {
          key: item.conversation_key,
          commId: row.id,
          item,
          kind: mine ? 'mine' : 'unclaimed',
          claim: null,
          sound: prefs.sound_enabled,
          count: 1,
          firstAt: item.last_message_at,
        },
        item,
        prefs,
      );
    };

    // A reply, a Done or a snooze after the toast went up takes it down.
    // At most one read per 5s, whatever the event rate (T-060). Not paused
    // while hidden: the leader is often a background tab, and the toast it
    // clears is in another one.
    let recheckTimer: number | undefined;
    const recheck = () => {
      if (recheckTimer !== undefined) return;
      recheckTimer = window.setTimeout(async () => {
        recheckTimer = undefined;
        const keys = Array.from(active.keys());
        if (!keys.length) return;
        const { data } = await supabase
          .from('inbox_items')
          .select('conversation_key, is_unreplied, last_message_direction, snoozed_until')
          .in('conversation_key', keys);
        const handled = ((data ?? []) as {
          conversation_key: string;
          is_unreplied: boolean;
          last_message_direction: string | null;
          snoozed_until: string | null;
        }[]).filter((r) => !r.is_unreplied);
        if (!handled.length) return;
        // Who marked it done or snoozed it, for the toast's parting line.
        const dismissedKeys = handled
          .filter((r) => r.last_message_direction !== 'outbound')
          .map((r) => r.conversation_key);
        const dismissals = new Map<
          string,
          { dismissed_by_user_id: string | null; dismissed_by_name: string | null }
        >();
        if (dismissedKeys.length) {
          const { data: rows } = await supabase
            .from('inbox_dismissals')
            .select('conversation_key, dismissed_by_user_id, dismissed_by_name')
            .in('conversation_key', dismissedKeys);
          for (const d of (rows ?? []) as any[]) dismissals.set(d.conversation_key, d);
        }
        for (const r of handled) {
          active.delete(r.conversation_key);
          bus.broadcast({
            type: 'alert-clear',
            key: r.conversation_key,
            reason: handledReason(r, dismissals.get(r.conversation_key), me.id),
          });
        }
      }, RECHECK_MS);
    };

    // A lead on an unclaimed toast got an owner: say who, drop Take it.
    const onLeadUpdate = async (lead: { id?: string; owner_id?: string | null; owner_source?: string | null }) => {
      if (!lead?.id || !lead.owner_id) return;
      for (const [key, a] of Array.from(active.entries())) {
        if (a.alert.kind !== 'unclaimed' || a.alert.item.lead_id !== lead.id) continue;
        if (a.alert.claim?.id === lead.owner_id) continue;
        const claim: AlertClaim = {
          id: lead.owner_id,
          name: lead.owner_id === me.id ? me.name : await profileName(lead.owner_id),
          source: lead.owner_source ?? null,
        };
        a.alert = { ...a.alert, claim };
        bus.broadcast({ type: 'alert-update', key, claim });
      }
    };

    const channel = supabase
      .channel(`inbox-alerts-${bus.tabId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'communications' }, (p) =>
        void onInsert(p.new as CommunicationInsert),
      )
      .on('postgres_changes', { event: '*', schema: 'public', table: 'communications' }, recheck)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inbox_dismissals' }, recheck)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads' }, (p) =>
        void onLeadUpdate(p.new as any),
      )
      .subscribe();

    return () => {
      offBus();
      window.clearTimeout(recheckTimer);
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isLeader, me?.id]);
}
